import { useEffect, useId, useRef, useState, type ComponentType, type ReactNode, type RefObject } from "react";
import { useTranslation } from "react-i18next";
import { Icon } from "@majourneys/icons";
import { useNavigate } from "@tanstack/react-router";
import { mapEdenError } from "@majourneys/api-client";
import {
  earliestRideMinutes,
  isFullName,
  isRideTooSoon,
  pickTransferPackage,
  splitFullName,
  TRANSFER_MAX_GUESTS,
  transferFeePlaces,
  transferHasTypedPlace,
  transferOutsideFee,
  WHATSAPP_URL,
  type Lang,
  type TransferPlace,
  type TransferRouteConfig,
} from "@majourneys/shared";
import { useCheckoutApi } from "../api/checkout.api";
import { PhoneField } from "../internal-ui/phone-field";
import { phoneIsComplete } from "../lib/phone";
import { PW, pewterInset, pewterPanel } from "../internal-ui/pewter-shell";
import { PewterDateField, PewterTimeField } from "../internal-ui/pewter-pickers";
import { BookingSkinScope } from "../skin";
import { BookingConsentPrompt, useBookingTrack, useTransferPlaceField, type TransferPlaceFieldProps } from "../provider";
import { TransferVehicleLine } from "./transfer-price-summary";

/**
 * The transfer booking's state and rules, shared by every layout that sells a
 * ride: the stepped form (`TransferForm`, activity pages and city sites) and
 * the web app's ride card on the transport pages. One hook, so both
 * price, validate and send exactly the same booking.
 *
 * The price is the smallest vehicle that seats the party, plus the flat
 * outside-Marrakech fee when the accommodation, restaurant or club falls
 * outside the zone (the Agafay camp never counts; see `transferFeePlaces`).
 * The API recomputes both before it writes the booking; this is the quote,
 * shown in the guest's display currency and sent in dirhams.
 * Payment is on arrival, to the driver, like every activity.
 */

export interface TransferRouteOption {
  id: string;
  slug: string;
  title: string;
  cityName: string;
  categorySlug: string | null;
  config: TransferRouteConfig;
  packages: Array<{ id: string; name: string; maxGuests: number | null; price: number }>;
}

export interface TransferFormProps {
  routes: TransferRouteOption[];
  /** Pre-selected route; the route picker is hidden when there is only one route. */
  initialSlug?: string;
  /**
   * The route picked from outside the form (the hub's route chips). When it
   * changes, the form switches to it the same way its own picker does.
   */
  selectedSlug?: string;
  /** Called when the guest picks a route in the form's own picker. */
  onRouteChange?: (slug: string) => void;
  /** The party size to open on (carried over from the hub). Clamped to what the route seats. */
  initialPeople?: number;
  /** Called with the party size whenever it changes, so the page can mark the matching vehicle. */
  onPeopleChange?: (people: number) => void;
  lang: Lang;
}

/** A field that can hold a problem, in the order the stepped form lays them out. */
export type TransferIssue = "pickup" | "place" | "date" | "time" | "returnTime" | "people" | "name" | "email" | "phone";

export const TRANSFER_ISSUE_ORDER: readonly TransferIssue[] = [
  "pickup",
  "place",
  "date",
  "time",
  "returnTime",
  "people",
  "name",
  "email",
  "phone",
];

export interface UseTransferBookingArgs extends TransferFormProps {
  /**
   * The order the fields sit on the page, so a failed submit focuses the
   * first one the guest sees. Defaults to the stepped form's order.
   */
  issueOrder?: readonly TransferIssue[];
}

/**
 * One more booking of the same ride, made by the same "Request" press: the
 * same route, vehicle, party, places and guest, on its own day and time. The
 * web app's airport card uses it for "Leaving Marrakech too?": the ride from
 * the airport and the ride back to it are two real bookings.
 */
export interface TransferFollowUp {
  /** YYYY-MM-DD. */
  date: string;
  /** HH:MM, the pick-up. */
  time: string;
  /** Lines for our team, put before the guest's own notes on this booking. */
  teamNotes?: string;
}

export interface TransferSubmitOptions {
  /**
   * Lines for our team, put before the guest's own notes. The booking has no
   * field for some things a layout asks (the airport direction, a flight
   * number), so they travel in the notes, where the team reads them.
   */
  teamNotes?: string;
  /** More bookings to make after the first; see `TransferFollowUp`. */
  followUps?: readonly TransferFollowUp[];
}

/** The team's lines first, then what the guest typed; nothing when both are empty. */
export function joinTransferNotes(team: string | undefined, guest: string): string | undefined {
  return [team?.trim(), guest.trim()].filter(Boolean).join("\n") || undefined;
}

const EMAIL_RE = /^\S+@\S+\.\S+$/;
const REQUIRED = "transport.field_required";
/** A place field left with text but no choice: pick a suggestion or keep the text as typed. */
const PLACE_REQUIRED = "transport.error_place_pick";
/** A pick-up under two hours away (owner, 2026-09-30). */
export const TOO_SOON = "transport.error_too_soon";
/** English for the city sites, which ship no transport copy of their own. */
export const TOO_SOON_DEFAULT = "Rides need at least 2 hours' notice. Pick a later time, or message us on WhatsApp.";
/**
 * The last pick-up any of the ride pickers offers (23:30). A day whose
 * earliest bookable pick-up falls after it has nothing left to choose.
 */
const LAST_PICKUP_MINUTE = 23 * 60 + 30;

/** API refusal codes, mapped to our own copy. The server's English never reaches the page. */
const ERROR_KEYS: Record<string, string> = {
  transfer_wrong_vehicle: "transport.error_wrong_vehicle",
  transfer_time_required: "transport.error_time_required",
  transfer_place_required: "transport.error_place_required",
  transfer_return_time_required: "transport.error_return_time_required",
  transfer_place_too_far: "transport.error_place_too_far",
  transfer_too_many_guests: "transport.error_too_many_guests",
  transfer_invalid: "transport.error_invalid",
  transfer_too_soon: TOO_SOON,
  past_datetime: "transport.error_past_time",
  stop_sales_blocked: "transport.error_unavailable",
  booking_paused: "transport.error_unavailable",
};

/** The largest party the route's biggest vehicle seats. */
export function maxPeopleFor(route: Pick<TransferRouteOption, "packages"> | undefined): number {
  const seats = (route?.packages ?? []).map((p) => p.maxGuests ?? 0).filter((n) => n > 0);
  return seats.length ? Math.min(Math.max(...seats), TRANSFER_MAX_GUESTS) : TRANSFER_MAX_GUESTS;
}

/** Today in Morocco, as YYYY-MM-DD. */
function moroccoToday(): string {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: "Africa/Casablanca",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date());
}

/** The day after `iso` (YYYY-MM-DD). */
function nextDay(iso: string): string {
  const [y, m, d] = iso.split("-").map(Number);
  return new Date(Date.UTC(y!, m! - 1, d! + 1)).toISOString().slice(0, 10);
}

/**
 * Does `day` still have a pick-up at least two hours away? Late in the
 * evening today has none left, and the calendars stop offering it.
 */
export function dayHasRideTimes(day: string, now: Date = new Date()): boolean {
  return earliestRideMinutes(day, now) <= LAST_PICKUP_MINUTE;
}

/** A party size the route can open on: a whole number from 1 to what its biggest vehicle seats. */
function openingPeople(wanted: number | undefined): number {
  const n = wanted !== undefined && Number.isInteger(wanted) && wanted >= 1 ? wanted : 2;
  // A bigger party than the largest vehicle opens as asked, so the form says
  // "message us on WhatsApp" instead of quietly booking 17 of 22 people.
  return Math.min(n, MAX_OPENING_PEOPLE);
}

/** The most people a link may open the form on; anything past the largest vehicle shows the WhatsApp note. */
const MAX_OPENING_PEOPLE = 99;

/** How long "Request sent" shows on the button before the thank-you page. */
const SENT_BEAT_MS = 650;

export function useTransferBooking({
  routes,
  initialSlug,
  selectedSlug,
  onRouteChange,
  initialPeople,
  onPeopleChange,
  lang,
  issueOrder = TRANSFER_ISSUE_ORDER,
}: UseTransferBookingArgs) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const track = useBookingTrack();
  const checkoutApi = useCheckoutApi();
  const PlaceField = useTransferPlaceField();
  const uid = useId();
  const rootRef = useRef<HTMLDivElement>(null);
  const inFlight = useRef(false);

  const [slug, setSlug] = useState(selectedSlug ?? initialSlug ?? routes[0]?.slug ?? "");
  const route = routes.find((r) => r.slug === slug) ?? routes[0];
  const [people, setPeople] = useState(() => openingPeople(initialPeople));
  useEffect(() => {
    onPeopleChange?.(people);
    // Only the count; a new callback identity is not a change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [people]);
  const [date, setDate] = useState("");
  const [time, setTime] = useState("");
  const [returnTime, setReturnTime] = useState("");
  /* "Not decided yet": the guest sends the return time on WhatsApp later. */
  const [returnLater, setReturnLater] = useState(false);
  const [pickup, setPickup] = useState<TransferPlace | null>(null);
  const [place, setPlace] = useState<TransferPlace | null>(null);
  const [form, setForm] = useState({ name: "", email: "", phone: "", notes: "" });
  const [touched, setTouched] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  /* The beat of "Request sent" on the button before the thank-you page, so
     the press visibly lands (owner, 2026-10-05, as in the booking sheet). */
  const [sent, setSent] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  /* References this press already booked. With follow-ups, a failure part
     way leaves the first rides booked: a retry books only the rest, never
     the same ride twice. */
  const [booked, setBooked] = useState<string[]>([]);
  /* Set after mount, not at render: the server and the browser can disagree
     on "today", and a differing attribute breaks hydration. */
  const [minDate, setMinDate] = useState<string | undefined>(undefined);
  useEffect(() => setMinDate(moroccoToday()), []);

  /* Switch routes, clearing what no longer fits the new one. */
  function applyRoute(nextSlug: string) {
    const next = routes.find((r) => r.slug === nextSlug);
    if (!next || !route) return;
    const placeChanged = next.config.secondPlace !== route.config.secondPlace;
    if (placeChanged) setPlace(null);
    if (placeChanged || next.config.trip !== route.config.trip) {
      setReturnTime("");
      setReturnLater(false);
    }
    setPeople((n) => Math.min(n, maxPeopleFor(next)));
    setSlug(nextSlug);
  }

  /* A route picked outside the form: adopted during render (React's
     "adjust state when a prop changes" pattern), so there is no frame with
     the old route's price showing. */
  const [syncedSlug, setSyncedSlug] = useState(selectedSlug);
  if (selectedSlug !== syncedSlug) {
    setSyncedSlug(selectedSlug);
    if (selectedSlug && selectedSlug !== slug) applyRoute(selectedSlug);
  }

  if (!route) return null;

  /* Read again on every render once mounted, so a page left open past
     midnight moves "today" with the clock instead of keeping yesterday. */
  const today = minDate ? moroccoToday() : undefined;
  /* The first day a ride can still be booked for: today, unless the two
     hours' notice has already run past its last pick-up. */
  const firstRideDay = today ? (dayHasRideTimes(today) ? today : nextDay(today)) : undefined;
  /* A pick-up on `day` at `at` that is under two hours away. Only once
     mounted: the server and the browser can disagree on the clock. */
  const tooSoon = (day: string, at: string) =>
    Boolean(today) && Boolean(day) && Boolean(at) && isRideTooSoon({ date: day, time: at });
  const maxPeople = maxPeopleFor(route);
  const pkg = pickTransferPackage(route.packages, people);
  const secondKind = route.config.secondPlace === "none" ? null : route.config.secondPlace;
  const needsReturn = route.config.trip === "return";
  const feePlaces = transferFeePlaces(route.config.secondPlace, pickup, place);
  const fee = transferOutsideFee(feePlaces);
  /* A typed place has no pin, so its fee is unknown until the team checks it
     on WhatsApp. Nothing is added; the summary says so instead. Moot once a
     pinned place already adds the fee, which is charged once per booking. */
  const typedFeeNote = fee === 0 && transferHasTypedPlace(feePlaces);
  const total = (pkg?.price ?? 0) + fee;
  /* No vehicle seats the party. Past the fleet's 17 seats it is the "bigger
     groups" message; a route whose largest vehicle is smaller says so. */
  const tooManyKey = people > TRANSFER_MAX_GUESTS ? "transport.error_too_many_guests" : "transport.group_too_large";

  /* Values are translation keys. */
  const email = form.email.trim();
  const issues: Record<TransferIssue, string | null> = {
    pickup: !pickup ? PLACE_REQUIRED : null,
    place: secondKind && !place ? PLACE_REQUIRED : null,
    // No past dates, ever: the picker cannot pick one, and this catches a
    // date that became past while the page sat open overnight.
    date: !date ? REQUIRED : today && date < today ? "transport.error_past_date" : null,
    time: !time ? REQUIRED : tooSoon(date, time) ? TOO_SOON : null,
    returnTime: needsReturn && !returnTime && !returnLater ? REQUIRED : null,
    people: !pkg ? tooManyKey : null,
    name: !form.name.trim() ? "form.full_name_required" : !isFullName(form.name) ? "form.full_name_incomplete" : null,
    email: !email ? "form.email_required" : !EMAIL_RE.test(email) ? "form.email_invalid" : null,
    phone: !form.phone.trim() ? "form.phone_required" : !phoneIsComplete(form.phone) ? "form.phone_invalid" : null,
  };
  const firstIssue = issueOrder.find((k) => issues[k]);
  const ok = pkg != null && !firstIssue;

  const shown = (k: TransferIssue) => (touched ? issues[k] : null);
  const hintId = (k: TransferIssue) => `${uid}-${k}-hint`;
  const invalidProps = (k: TransferIssue) =>
    shown(k) ? { "aria-invalid": true as const, "aria-describedby": hintId(k) } : {};
  const hint = (k: TransferIssue): ReactNode =>
    shown(k) ? (
      <p id={hintId(k)} className="mt-1 text-[12px]" style={{ color: PW.red }}>
        {t(shown(k)!, shown(k) === TOO_SOON ? { defaultValue: TOO_SOON_DEFAULT } : undefined)}
      </p>
    ) : null;

  function changeRoute(nextSlug: string) {
    applyRoute(nextSlug);
    onRouteChange?.(nextSlug);
  }

  function focusField(k: TransferIssue) {
    const el = rootRef.current?.querySelector<HTMLElement>(`[data-field="${k}"]`);
    if (!el) return;
    const target = el.matches("input, select, textarea, button")
      ? el
      : el.querySelector<HTMLElement>("input, select, textarea, button");
    (target ?? el).focus();
  }

  async function submit(options: TransferSubmitOptions = {}) {
    if (inFlight.current) return;
    setTouched(true);
    if (firstIssue) {
      focusField(firstIssue);
      return;
    }
    /* `issues` is from the last render; if midnight has passed since, the
       date is past now even though nothing re-rendered. The re-render that
       `touched` triggers shows the hint. */
    if (date < moroccoToday()) {
      focusField("date");
      return;
    }
    /* Same for the two hours' notice: the clock moves while the page sits. */
    if (isRideTooSoon({ date, time })) {
      focusField("time");
      return;
    }
    if ((options.followUps ?? []).some((f) => isRideTooSoon({ date: f.date, time: f.time }))) {
      setSubmitError(t(TOO_SOON, { defaultValue: TOO_SOON_DEFAULT }));
      return;
    }
    if (!route || !ok || !pkg || !pickup) return;
    inFlight.current = true;
    setSubmitting(true);
    setSubmitError(null);
    const { firstName, lastName } = splitFullName(form.name);
    const rides = [
      { date, time, notes: joinTransferNotes(options.teamNotes, form.notes) },
      ...(options.followUps ?? []).map((f) => ({ date: f.date, time: f.time, notes: joinTransferNotes(f.teamNotes, form.notes) })),
    ];
    const refs = [...booked];
    try {
      for (let i = refs.length; i < rides.length; i++) {
        const ride = rides[i]!;
        const result = await checkoutApi.submit({
          activityId: route.id,
          activitySlug: route.slug,
          packageId: pkg.id,
          date: ride.date,
          time: ride.time,
          adults: people,
          children: 0,
          infants: 0,
          // A vehicle is one price for the whole party.
          groups: 1,
          transport: false,
          transportOutside: false,
          firstName,
          lastName,
          email,
          phone: form.phone.trim(),
          paymentMethod: "arrival",
          notes: ride.notes,
          total,
          lang,
          transfer: {
            pickup,
            ...(secondKind && place ? { place } : {}),
            ...(needsReturn ? (returnTime ? { returnTime } : { returnTimeLater: true }) : {}),
          },
        });
        // The reference the thank-you page is keyed by (as the basket checkout reads it).
        refs.push(String(result.bookingId ?? result.id));
        setBooked([...refs]);
        track("booking_submitted", {
          activity_id: route.id,
          activity_slug: route.slug,
          city: route.cityName,
          category: route.categorySlug ?? undefined,
          package_id: pkg.id,
          package_name: pkg.name,
          booking_id: result.bookingId,
          total,
          currency: "MAD",
          payment: "arrival",
          flow: "transfer",
        });
      }
      // The guard stays up: the page is leaving for the thank-you page. Any
      // other ride's reference travels in `?also=`, which the thank-you page
      // lists under the first booking, as it does after a basket checkout.
      setSent(true);
      await new Promise((resolve) => window.setTimeout(resolve, SENT_BEAT_MS));
      const [first, ...others] = refs;
      const to = `/${lang}/booking/${first}/thank-you` as never;
      navigate(others.length > 0 ? { to, search: { also: others.join(",") } as never } : { to });
    } catch (e) {
      inFlight.current = false;
      const { code } = mapEdenError(e);
      const reason = t(ERROR_KEYS[code] ?? "transport.booking_failed", code === "transfer_too_soon" ? { defaultValue: TOO_SOON_DEFAULT } : undefined);
      /* Some rides went through: say so, so the guest retries only the rest. */
      setSubmitError(
        refs.length > 0
          ? `${t("transport.followup_failed", { defaultValue: "Your first ride is booked. The next one did not go through." })} ${reason}`
          : reason,
      );
    } finally {
      setSubmitting(false);
    }
  }

  /* Rides need two hours' notice (owner, 2026-09-30): on the first bookable
     day, every pick-up before now + 2 hours is off, and the server refuses
     them too. `pickupNotBefore` is the last minute that is off, for pickers
     that disable "at or before"; null when the whole day is open. */
  const earliest = today && date ? earliestRideMinutes(date) : 0;
  const pickupNotBefore = earliest > 0 ? earliest - 1 : null;

  function chooseDate(next: string) {
    setDate(next);
    if (time && tooSoon(next, time)) setTime("");
  }

  function chooseReturnTime(v: string) {
    setReturnTime(v);
    setReturnLater(false);
  }

  function chooseReturnLater() {
    setReturnTime("");
    setReturnLater(true);
  }

  return {
    lang,
    uid,
    rootRef: rootRef as RefObject<HTMLDivElement>,
    PlaceField: PlaceField as ComponentType<TransferPlaceFieldProps> | undefined,
    routes,
    route,
    changeRoute,
    people,
    setPeople,
    maxPeople,
    pkg,
    secondKind,
    needsReturn,
    fee,
    typedFeeNote,
    total,
    tooManyKey,
    today,
    firstRideDay,
    /** A pick-up at `at` on `day` would be under two hours away. */
    tooSoon,
    date,
    chooseDate,
    time,
    setTime,
    pickupNotBefore,
    returnTime,
    returnLater,
    chooseReturnTime,
    chooseReturnLater,
    pickup,
    setPickup,
    place,
    setPlace,
    form,
    setForm,
    touched,
    /** Shows every field's problem, as a failed "Request" does, without sending. */
    markTouched: () => setTouched(true),
    /** References a press that failed part way already booked (see `TransferSubmitOptions.followUps`). */
    booked,
    issues,
    firstIssue,
    shown,
    hintId,
    invalidProps,
    hint,
    submitting,
    sent,
    submitError,
    submit,
  };
}

export type TransferBooking = NonNullable<ReturnType<typeof useTransferBooking>>;

/* 16px text on every field: anything smaller makes iOS Safari zoom on focus.
   `.mj-pw-field` paints the fill and edge and owns focus / invalid states. */
const FIELD = "mj-pw-field h-11 w-full px-3 text-[16px] outline-none";
const FIELD_STYLE = { borderRadius: 14, color: PW.navy } as const;

/**
 * Without a place picker the pick-up can't be pinned, so nothing could be
 * booked: say so and hand over to WhatsApp rather than show a form whose
 * Book button can only fail.
 */
export function TransferWhatsAppOnly() {
  const { t } = useTranslation();
  return (
    <BookingSkinScope skin="pewter">
      <div style={{ ...pewterPanel(22), color: PW.navy }} className="p-4 font-sans text-[14px]" data-testid="transfer-form">
        <p className="mb-2">{t("transport.whatsapp_only")}</p>
        <a href={WHATSAPP_URL} className="inline-flex min-h-6 items-center font-semibold underline">{t("transport.whatsapp")}</a>
      </div>
    </BookingSkinScope>
  );
}

/**
 * One place field: the guest's accommodation, or the route's second place
 * (restaurant, club or camp). Renders nothing for a second place the route
 * does not take.
 */
export function TransferPlaceBlock({ b, which }: { b: TransferBooking; which: "pickup" | "place" }) {
  const { t } = useTranslation();
  const PlaceField = b.PlaceField;
  if (!PlaceField) return null;
  if (which === "place" && !b.secondKind) return null;
  const label = which === "pickup" ? t("transport.place_pickup") : t(`transport.place_${b.secondKind}`);
  return (
    <div className="mb-3 min-w-0 last:mb-0">
      <div data-field={which} tabIndex={-1} role="group" aria-label={label} aria-describedby={b.shown(which) ? b.hintId(which) : undefined} className="outline-none">
        {which === "pickup" ? (
          <PlaceField kind="pickup" label={label} value={b.pickup} onChange={b.setPickup} invalid={Boolean(b.shown("pickup"))} />
        ) : (
          <PlaceField key={b.route.slug} kind={b.secondKind!} label={label} value={b.place} onChange={b.setPlace} invalid={Boolean(b.shown("place"))} />
        )}
      </div>
      {b.hint(which)}
    </div>
  );
}

/** The date, the pick-up time and, on a there-and-back ride, the return time. */
export function TransferWhenFields({ b }: { b: TransferBooking }) {
  const { t } = useTranslation();
  const { uid } = b;
  return (
    <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
      <PickerLabelled id={`${uid}-date-label`} text={t("transport.date")} hint={b.hint("date")}>
        <PewterDateField
          labelId={`${uid}-date-label`}
          title={t("transport.date")}
          value={b.date}
          onChange={b.chooseDate}
          minDate={b.firstRideDay}
          lang={b.lang}
          placeholder={t("transport.choose_date")}
          fieldProps={{ "data-field": "date", ...b.invalidProps("date") }}
        />
      </PickerLabelled>
      <PickerLabelled id={`${uid}-time-label`} text={t("transport.pickup_time")} hint={b.hint("time")}>
        <PewterTimeField
          labelId={`${uid}-time-label`}
          title={t("transport.pickup_time")}
          value={b.time}
          onChange={b.setTime}
          notBefore={b.pickupNotBefore}
          placeholder={t("transport.choose_time")}
          fieldProps={{ "data-field": "time", ...b.invalidProps("time") }}
        />
      </PickerLabelled>
      {b.needsReturn && (
        <PickerLabelled
          id={`${uid}-return-label`}
          text={t("transport.return_time")}
          className="sm:col-span-2"
          hint={
            b.hint("returnTime") ??
            (b.returnLater ? (
              <p className="mt-1 text-[12px]" style={{ color: PW.muted }}>{t("transport.return_time_later_hint")}</p>
            ) : null)
          }
        >
          {/* No past-time limit: a return can fall after midnight. */}
          <PewterTimeField
            labelId={`${uid}-return-label`}
            title={t("transport.return_time")}
            value={b.returnTime}
            onChange={b.chooseReturnTime}
            placeholder={t("transport.choose_time")}
            later={{ label: t("transport.return_time_later"), chosen: b.returnLater, onChoose: b.chooseReturnLater }}
            fieldProps={{ "data-field": "returnTime", ...b.invalidProps("returnTime") }}
          />
        </PickerLabelled>
      )}
    </div>
  );
}

/**
 * The party size stepper. One past the largest vehicle is allowed, to say
 * what to do then (WhatsApp). With `showVehicle` the vehicle it picked sits
 * right under it; a layout that shows the vehicle elsewhere leaves it out.
 */
export function TransferPeopleField({ b, showVehicle = true }: { b: TransferBooking; showVehicle?: boolean }) {
  const { t } = useTranslation();
  const { uid, people, maxPeople, pkg } = b;
  const peopleLabelId = `${uid}-people`;
  return (
    <div className="min-w-0">
      <div
        role="group"
        aria-labelledby={peopleLabelId}
        aria-describedby={!pkg ? `${uid}-people-note` : undefined}
        data-field="people"
        tabIndex={-1}
        className="mj-pw-field flex h-12 items-center justify-between px-1.5 outline-none"
        style={FIELD_STYLE}
      >
        <span id={peopleLabelId} className="ps-2 text-[15px] font-semibold">{t("transport.people")}</span>
        <span className="flex items-center gap-2">
          <button type="button" className="mj-pw-chip h-9 w-9 text-[16px] disabled:opacity-40" aria-label={t("transport.remove_person")} disabled={people <= 1} onClick={() => b.setPeople((n) => Math.max(1, n - 1))}>
            <span aria-hidden>−</span>
          </button>
          <span aria-live="polite" className="min-w-6 text-center text-[16px] font-semibold">{people}</span>
          <button type="button" className="mj-pw-chip h-9 w-9 text-[16px] disabled:opacity-40" aria-label={t("transport.add_person")} disabled={people > maxPeople} onClick={() => b.setPeople((n) => Math.min(maxPeople + 1, n + 1))}>
            <span aria-hidden>+</span>
          </button>
        </span>
      </div>
      {pkg ? (
        showVehicle ? (
          <div className="mt-2 rounded-xl px-3 py-2" style={pewterInset}>
            <TransferVehicleLine maxGuests={pkg.maxGuests ?? people} />
            <p className="mt-1 text-[12px]" style={{ color: PW.muted }}>
              {t("transport.vehicle_auto", { defaultValue: "Chosen for your group. The price is for the whole vehicle." })}
            </p>
          </div>
        ) : null
      ) : (
        <div id={`${uid}-people-note`} className="mt-2" data-testid="transfer-too-many">
          <p className="text-[13px]" style={{ color: PW.red }}>{t(b.tooManyKey)}</p>
          <a href={WHATSAPP_URL} className="mj-pw-chip mt-2 inline-flex min-h-10 items-center gap-1.5 px-4 text-[14px] font-semibold">
            <Icon name="ri-whatsapp-line" className="text-[16px]" style={{ color: PW.green }} aria-hidden="true" />
            {t("transport.whatsapp_button", { defaultValue: "Message us on WhatsApp" })}
          </a>
        </div>
      )}
    </div>
  );
}

/**
 * The guest's name, email, phone (WhatsApp) and notes. `phoneFirst` puts the
 * phone right after the name, as the web app's ride card does.
 */
export function TransferDetailsFields({ b, phoneFirst = false }: { b: TransferBooking; phoneFirst?: boolean }) {
  const { t } = useTranslation();
  const { form, setForm } = b;
  const emailField = (
    <Labelled key="email" text={t("transport.email")} hint={b.hint("email")}>
      <input type="email" dir="ltr" data-field="email" className={FIELD} style={FIELD_STYLE} autoComplete="email" {...b.invalidProps("email")} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
    </Labelled>
  );
  const phoneField = (
    <div key="phone" className="min-w-0">
      <span className="mb-1 block text-[12px]" style={{ color: PW.muted }}>
        {t("transport.phone_whatsapp", { defaultValue: "WhatsApp or phone" })}
      </span>
      {/* Forced LTR so a dial code and a number stay readable on an Arabic page. */}
      <div dir="ltr" data-field="phone" aria-describedby={b.shown("phone") ? b.hintId("phone") : undefined}>
        <PhoneField value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} hasError={Boolean(b.shown("phone"))} />
      </div>
      {b.hint("phone")}
    </div>
  );
  return (
    <div className="grid gap-2">
      <Labelled text={t("transport.full_name")} hint={b.hint("name")}>
        <input data-field="name" className={FIELD} style={FIELD_STYLE} autoComplete="name" {...b.invalidProps("name")} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
      </Labelled>
      {phoneFirst ? [phoneField, emailField] : [emailField, phoneField]}
      <textarea className="mj-pw-field min-h-[64px] w-full px-3 py-2 text-[16px] outline-none" style={FIELD_STYLE} aria-label={t("transport.notes")} placeholder={t("transport.notes")} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
    </div>
  );
}

/** One announcement for a failed attempt (the per-field hints are linked, not announced), and the API's refusal. */
export function TransferAlerts({ b }: { b: TransferBooking }) {
  const { t } = useTranslation();
  return (
    <>
      {b.touched && b.firstIssue && (
        <p role="alert" className="mt-2 text-[13px]" style={{ color: PW.red }}>
          {t(b.issues[b.firstIssue]!, b.issues[b.firstIssue] === TOO_SOON ? { defaultValue: TOO_SOON_DEFAULT } : undefined)}
        </p>
      )}
      {b.submitError && <p role="alert" className="mt-2 text-[13px]" style={{ color: PW.red }}>{b.submitError}</p>}
      <div className="mt-3">
        <BookingConsentPrompt />
      </div>
    </>
  );
}

/**
 * A visible label for the pewter pickers. Their triggers are buttons, which a
 * wrapping <label> would rename, so the label is a plain element with an id
 * that the trigger points at with aria-labelledby.
 */
export function PickerLabelled({ id, text, className, hint, children }: { id: string; text: string; className?: string; hint?: ReactNode; children: ReactNode }) {
  // min-w-0: Safari sizes a grid item to its button's full text, so without
  // it "Choose a date" widens the whole form past the screen on iPhone.
  return (
    <div className={`min-w-0 ${className ?? ""}`}>
      <span id={id} className="mb-1 block text-[12px]" style={{ color: PW.muted }}>{text}</span>
      {children}
      {hint}
    </div>
  );
}

/** A visible label around a field, with its hint outside the label so the field's name stays clean. */
function Labelled({ text, className, hint, children }: { text: string; className?: string; hint?: ReactNode; children: ReactNode }) {
  return (
    <div className={`min-w-0 ${className ?? ""}`}>
      <label className="block">
        <span className="mb-1 block text-[12px]" style={{ color: PW.muted }}>{text}</span>
        {children}
      </label>
      {hint}
    </div>
  );
}
